// "Back to <site>" for a project opened from a site (AGENTS.md §3: the site links to /projects/<id>/?from=/sites/<site>/#<part>,
// and keeps the same in this tab's session as "site-return", so the way back survives a fresh load), and "Back to the lab" for
// one opened from the lab's catalogue or by its address (#431): every project has a way out. Shared by every project: its top bar
// shows backLink(id) and needs nothing else. Only a path on this site, under /sites/, or the lab is ever followed.

/** The sites' names as the link says them, by their path. */
const SITE_NAMES = { "/sites/works/": "the works", "/sites/bess/": "the battery site", "/sites/datacentre/": "the data centre" };
const KEY = "site-return";

/** A path the link may go to: on this site, a site's own page, nothing else (never another origin, never a script). */
export function safeReturn(to) {
  if (typeof to !== "string" || !/^\/sites\/[a-z0-9-]+\/(#[\w:.-]*)?$/i.test(to)) return null;
  return to;
}

/** Where "Back" goes from this project, and what it says; null when the project was not opened from a site. */
export function siteReturn(projectId, { search = typeof location !== "undefined" ? location.search : "", store = typeof sessionStorage !== "undefined" ? sessionStorage : null } = {}) {
  let to = safeReturn(new URLSearchParams(search).get("from"));
  if (!to) { try { const s = JSON.parse(store?.getItem(KEY) ?? "null"); if (s && s.project === projectId) to = safeReturn(s.to); } catch { /* none, or unreadable */ } }
  if (!to) return null;
  const site = to.slice(0, to.indexOf("/", "/sites/".length) + 1);
  return { href: to, label: `Back to ${SITE_NAMES[site] ?? "the site"}` };
}

/** Forgets the way back to a site: the shell calls it when a project is opened from anywhere but a site (the lab, the map), so an
 *  earlier visit's "Back to the works" never stands where "Back to the lab" belongs. A fresh load keeps it. */
export function forgetSiteReturn(store = typeof sessionStorage !== "undefined" ? sessionStorage : null) {
  try { store?.removeItem(KEY); } catch { /* storage refused: nothing kept to forget */ }
}

/** Where a project opened from anywhere but a site goes back to. */
export const LAB_RETURN = { href: "/lab/", label: "Back to the lab" };

/** What the link shows: the place only, after its chevron ("Lab", "The works"), as the reference pictures do (#797, #1038); its
 *  name for screen readers stays the whole "Back to the lab", which holds the shown words. */
export const shortLabel = label => { const w = label.replace(/^Back to /, ""); return w === "the lab" ? "Lab" : w[0].toUpperCase() + w.slice(1); };

/** The link for a project's top bar: back to the site it was opened from, else back to the lab. It stands at the bar's left end.
 *  The way back to the lab is the word "Lab" itself, on a laptop as on a phone (owner, 27 October, #3447, #3449: "keep same Lab icon
 *  for laptop as for mobile"), a pill in the bar's family; the way back to a site keeps its arrow, the site's word under it on hover
 *  and focus (look.css). Either is named in full ("Back to the lab"), which holds the shown word. */
export function backLink(projectId, opts) {
  const r = siteReturn(projectId, opts);
  if (!r) return `<a class="btn sm back lab" href="${LAB_RETURN.href}" aria-label="${LAB_RETURN.label}">Lab</a>`;
  return `<a class="btn sm back" href="${r.href}" aria-label="${r.label}"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5M11 6l-6 6 6 6"/></svg><span class="bl">${shortLabel(r.label)}</span></a>`;
}
